import { useState, useEffect } from "react";
import API from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Shield, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetUrl, setResetUrl] = useState(null);

  useEffect(() => {
    API.get("/auth/config").then(({ data }) => setResetUrl(data.reset_password_url)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "vendor") navigate("/vendor");
      else navigate("/management");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6 animate-in">
          <img
            src="https://customer-assets.emergentagent.com/job_apex-merchants/artifacts/6e4jsq53_New%20FieldProIQ%20logo.PNG"
            alt="Apex FieldProIQ"
            className="w-full max-w-md h-auto mx-auto mb-2 object-contain"
            data-testid="login-logo"
          />
        </div>

        <Card className="border border-zinc-800 bg-zinc-900 shadow-2xl shadow-black/40 animate-in animate-delay-1">
          <CardHeader className="pb-4">
            <h2 className="text-lg font-heading font-semibold text-white">Sign In</h2>
            <p className="text-sm text-zinc-400">Use your APEX Business Intelligence email and password</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-950/50 border border-red-800 text-sm text-red-400" data-testid="login-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-zinc-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-red-500 focus:ring-red-500/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-zinc-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-11 pr-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-red-500 focus:ring-red-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-medium"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>Sign In <ArrowRight className="ml-2 w-4 h-4" /></>
                )}
              </Button>
            </form>
            {resetUrl && (
              <a href={resetUrl} target="_blank" rel="noreferrer"
                 className="block text-center text-xs text-zinc-400 hover:text-white mt-4"
                 data-testid="login-forgot-password-link">
                Forgot password?
              </a>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-zinc-600 mt-6 font-body">
          APEX FieldProIQ
        </p>
        <div className="flex items-center justify-center gap-4 mt-2 text-[11px]" data-testid="login-legal-links">
          <a href="https://apexinfinityventures.com/privacy" target="_blank" rel="noreferrer"
             className="text-zinc-500 hover:text-zinc-300" data-testid="login-privacy-link">
            Privacy Policy
          </a>
          <span className="text-zinc-700">·</span>
          <a href="https://apexinfinityventures.com/terms" target="_blank" rel="noreferrer"
             className="text-zinc-500 hover:text-zinc-300" data-testid="login-terms-link">
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );
}
